import { useMemo, useState } from "react";
import { useLang } from "@/lib/i18n";
import Icon from "@/components/shared/Icon";

const WORDS = {
  easy: [
    { w: "CAT", hint: { en: "A furry pet that says meow", zh: "一种会喵喵叫的毛茸茸宠物" } },
    { w: "SUN", hint: { en: "It shines in the sky by day", zh: "白天在天空中发光" } },
    { w: "BOOK", hint: { en: "You read it to learn", zh: "你读它来学习" } },
    { w: "STAR", hint: { en: "It twinkles at night", zh: "夜晚闪烁的东西" } },
  ],
  medium: [
    { w: "APPLE", hint: { en: "A red or green fruit", zh: "红色或绿色的水果" } },
    { w: "HAPPY", hint: { en: "The feeling of joy", zh: "快乐的感觉" } },
    { w: "LEARN", hint: { en: "To gain new knowledge", zh: "获得新知识" } },
    { w: "SMILE", hint: { en: "You do it when glad", zh: "开心时你会做的事" } },
  ],
  hard: [
    { w: "GARDEN", hint: { en: "Where flowers grow", zh: "花朵生长的地方" } },
    { w: "FRIEND", hint: { en: "Someone you like and trust", zh: "你喜欢并信任的人" } },
    { w: "PLANET", hint: { en: "Earth is one of these", zh: "地球就是其中之一" } },
    { w: "KINDNESS", hint: { en: "Being gentle and caring", zh: "温柔和关怀" } },
  ],
};

function shuffle(a) {
  const arr = [...a];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function WordBuilder({ onFinish, difficulty = "easy" }) {
  const { t, code } = useLang();
  const rounds = useMemo(() => shuffle(WORDS[difficulty] || WORDS.easy), [difficulty]);
  const [round, setRound] = useState(0);
  const [tiles, setTiles] = useState(() => shuffle((rounds[0]?.w || "").split("").map((c, i) => ({ c, id: i }))));
  const [order, setOrder] = useState([]);
  const [checked, setChecked] = useState(null);
  const [score, setScore] = useState(0);

  const target = rounds[round].w;
  const built = order.map((id) => tiles.find((x) => x.id === id).c).join("");

  const reshuffleFor = (idx) => {
    setTiles(shuffle(rounds[idx].w.split("").map((c, i) => ({ c, id: i }))));
    setOrder([]);
    setChecked(null);
  };

  const toggle = (id) => {
    if (checked) return;
    setOrder((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));
  };

  const check = () => {
    const ok = built === target;
    if (ok) setScore((s) => s + 1);
    setChecked(ok ? "ok" : "no");
  };

  const next = () => {
    if (round + 1 >= rounds.length) {
      onFinish(score, rounds.length);
      return;
    }
    const n = round + 1;
    setRound(n);
    reshuffleFor(n);
  };

  return (
    <div>
      <div className="flex items-center justify-between text-sm font-bold text-[#64748B]">
        <span>{t("play.question")} {round + 1} {t("play.of")} {rounds.length}</span>
        <span className="inline-flex items-center gap-1.5 text-[#147DEB]"><Icon name="Star" className="h-4 w-4 text-[#FFC928]" /> {t("common.score")}: {score}</span>
      </div>

      <div className="mt-5 rounded-2xl bg-[#E8F5FF]/60 p-4 text-center">
        <p className="text-sm font-semibold text-[#475569]">{rounds[round].hint[code]}</p>
      </div>

      {/* built word slots */}
      <div className="mt-5 flex min-h-[64px] flex-wrap items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-[#147DEB]/25 p-3">
        {order.length === 0 && <span className="text-sm text-[#94A3B8]">{code === "zh" ? "点击下方字母开始拼写" : "Tap the letters below to spell"}</span>}
        {order.map((id) => {
          const tile = tiles.find((x) => x.id === id);
          return (
            <button key={id} onClick={() => toggle(id)} data-testid={`wb-built-${id}`} className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#147DEB] text-2xl font-extrabold text-white">
              {tile.c}
            </button>
          );
        })}
      </div>

      {/* available tiles */}
      <div className="mt-4 flex flex-wrap justify-center gap-2">
        {tiles.map((tile) => (
          <button
            key={tile.id}
            onClick={() => toggle(tile.id)}
            disabled={order.includes(tile.id) || !!checked}
            data-testid={`wb-tile-${tile.id}`}
            className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 text-2xl font-extrabold transition-all ${
              order.includes(tile.id) ? "border-transparent bg-[#E8F5FF] text-[#CBD5E1]" : "border-[#FFC928] bg-white text-[#082B66] hover:-translate-y-0.5"
            }`}
          >
            {tile.c}
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center justify-between">
        {checked ? (
          <span className={`font-bold ${checked === "ok" ? "text-emerald-600" : "text-rose-500"}`} data-testid="wb-feedback">
            {checked === "ok" ? t("common.correct") : `${t("common.incorrect")} (${target})`}
          </span>
        ) : (
          <span />
        )}
        {checked ? (
          <button onClick={next} data-testid="wb-next-button" className="inline-flex items-center gap-1.5 rounded-2xl bg-[#147DEB] px-6 py-2.5 font-bold text-white hover:bg-[#0F62BD]">
            {round + 1 >= rounds.length ? t("play.summary") : t("common.next")} <Icon name="ArrowRight" className="h-4 w-4" />
          </button>
        ) : (
          <button onClick={check} disabled={order.length !== target.length} data-testid="wb-check-button" className="inline-flex items-center gap-1.5 rounded-2xl bg-[#45C878] px-6 py-2.5 font-bold text-white hover:bg-[#34B566] disabled:opacity-40">
            <Icon name="Check" className="h-4 w-4" /> {code === "zh" ? "检查" : "Check"}
          </button>
        )}
      </div>
    </div>
  );
}
